"use client"

import { useState, useEffect, useCallback } from "react"
import { motion, AnimatePresence, useSpring } from "framer-motion"
import { Laugh, RefreshCw, ThumbsUp, ThumbsDown, Download } from "lucide-react"

const ProgrammingMemesGenerator = () => {
  const [memes, setMemes] = useState([])
  const [currentMemeIndex, setCurrentMemeIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [likes, setLikes] = useState(0)
  const [dislikes, setDislikes] = useState(0)

  const likeScale = useSpring(1, { stiffness: 300, damping: 10 })
  const dislikeScale = useSpring(1, { stiffness: 300, damping: 10 })

  const fetchMemes = useCallback(async () => {
    setLoading(true)
    setError(null)

    const myHeaders = new Headers()
    myHeaders.append("x-apihub-key", "dC-X6X1n75W9ul7oEz2tOmvuaww1WLB3cGiKWsfsq057qO4-Ay")
    myHeaders.append("x-apihub-host", "Programming-Memes-Images.allthingsdev.co")
    myHeaders.append("x-apihub-endpoint", "c1752127-9642-42d2-935d-d93c18f65f2c")

    const requestOptions = {
      method: "GET",
      headers: myHeaders,
      redirect: "follow",
    }

    try {
      const response = await fetch(
        "https://Programming-Memes-Images.proxy-production.allthingsdev.co/v1/memes",
        requestOptions,
      )
      const result = await response.json()
      setMemes(result)
      setCurrentMemeIndex(0)
    } catch (error) {
      console.error("Error:", error)
      setError("Failed to fetch memes. Please try again later.")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchMemes()
  }, [fetchMemes])

  const handleNextMeme = () => {
    setCurrentMemeIndex((prevIndex) => (prevIndex + 1) % memes.length)
    setLikes(0)
    setDislikes(0)
  }

  const handlePrevMeme = () => {
    setCurrentMemeIndex((prevIndex) => (prevIndex - 1 + memes.length) % memes.length)
    setLikes(0)
    setDislikes(0)
  }

  const handleDownload = () => {
    if (memes[currentMemeIndex]) {
      window.open(memes[currentMemeIndex].image, "_blank")
    }
  }

  const handleLike = () => {
    setLikes(likes + 1)
    likeScale.set(1.2)
    setTimeout(() => likeScale.set(1), 100)
  }

  const handleDislike = () => {
    setDislikes(dislikes + 1)
    dislikeScale.set(1.2)
    setTimeout(() => dislikeScale.set(1), 100)
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8"
      >
        <Laugh className="inline-block text-yellow-500 mb-4" size={48} />
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white">Programming Memes Generator</h1>
        <p className="text-lg text-gray-600 dark:text-gray-300 mt-2">Get your daily dose of programming humor!</p>
      </motion.div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <RefreshCw className="animate-spin text-yellow-500" size={48} />
            </div>
          ) : error ? (
            <div className="text-center text-red-500">{error}</div>
          ) : memes.length > 0 ? (
            <AnimatePresence mode="wait">
              <motion.div
                key={currentMemeIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col items-center"
              >
                <img
                  src={memes[currentMemeIndex].image || "/placeholder.svg"}
                  alt="Programming Meme"
                  className="max-w-full h-auto rounded-lg shadow-md"
                />
                <div className="mt-4 flex justify-center space-x-4">
                  <button
                    onClick={handlePrevMeme}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
                  >
                    Previous
                  </button>
                  <button
                    onClick={handleNextMeme}
                    className="px-4 py-2 bg-yellow-500 text-white rounded-full hover:bg-yellow-600 transition-colors duration-200"
                  >
                    Next Meme
                  </button>
                </div>
                <div className="mt-4 flex justify-center space-x-4">
                  <motion.button
                    onClick={handleLike}
                    style={{ scale: likeScale }}
                    className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors duration-200 flex items-center"
                  >
                    <ThumbsUp size={20} />
                    <span className="ml-1">{likes}</span>
                  </motion.button>
                  <motion.button
                    onClick={handleDislike}
                    style={{ scale: dislikeScale }}
                    className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors duration-200 flex items-center"
                  >
                    <ThumbsDown size={20} />
                    <span className="ml-1">{dislikes}</span>
                  </motion.button>
                  <button
                    onClick={handleDownload}
                    className="p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors duration-200"
                  >
                    <Download size={20} />
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          ) : (
            <div className="text-center text-gray-500 dark:text-gray-400">No memes available</div>
          )}
        </div>
      </div>

      <motion.div
        className="mt-8 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.5 }}
      >
        <p className="text-gray-600 dark:text-gray-400 italic">
          "There are only 10 types of people in the world: those who understand binary, and those who don't."
        </p>
      </motion.div>
    </div>
  )
}

export default ProgrammingMemesGenerator

